const EARTH_RADIUS_METERS = 6378137;

/** Destination point at `distanceMeters` along `bearingDeg` (compass bearing,
 * 0 = north) from (lat, lon). Returns [lon, lat] to match GeoJSON ordering. */
export function destinationPoint(
  lat: number,
  lon: number,
  bearingDeg: number,
  distanceMeters: number
): [number, number] {
  const bearing = (bearingDeg * Math.PI) / 180;
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;

  const newLatRad = Math.asin(
    Math.sin(latRad) * Math.cos(angularDistance) +
      Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const newLonRad =
    lonRad +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRad),
      Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(newLatRad)
    );

  return [(newLonRad * 180) / Math.PI, (newLatRad * 180) / Math.PI];
}

function normalizeBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

export type PhotoSide = 'left' | 'right';

function isPhotoSide(value: string | null): value is PhotoSide {
  return value === 'left' || value === 'right';
}

// `deviceHeading` from the RTLS history is the compass bearing the phone was
// *moving* along while walking the row of trailers — not the direction the
// camera lens was pointed. The camera instead points out to whichever side
// of the truck the photo was taken from. Facing the direction of travel, the
// photographer's right side is +90° and their left side is -90° from that
// bearing (standard compass convention: 0 = N, 90 = E, clockwise-positive).
// An unrecognized or missing side falls back to the raw movement bearing,
// matching the old side-unaware behavior.
//
// `device_heading` is a `:string` column in flux (GraphQL exposes it as a
// String, not a Float — see rtls_history's schema), so `movementBearingDeg`
// arrives here as e.g. "149.3", not the number the type below promises. Left
// uncoerced, `"149.3" + -90` string-concatenates to `"149.3-90"`, which
// `normalizeBearing`'s `%` turns into NaN (silently dropping the cone), and
// `"149.3" + 90` concatenates to `"149.390"` (a wrong but finite heading) —
// so coerce explicitly rather than trust the caller's numeric type.
export function cameraHeading(movementBearingDeg: number, side: string | null): number {
  const bearing = Number(movementBearingDeg);
  if (!isPhotoSide(side)) return normalizeBearing(bearing);
  const offset = side === 'right' ? 90 : -90;
  return normalizeBearing(bearing + offset);
}

// Estimated horizontal field of view and viewing distance for the yard
// camera — not a per-device spec, just enough to sketch roughly what the
// camera was pointed at on the map.
export const FOV_DEGREES = 60;
export const FOV_RANGE_METERS = 35;

export function buildFovPolygon(
  lat: number,
  lon: number,
  headingDeg: number
): GeoJSON.Feature<GeoJSON.Polygon> {
  const left = destinationPoint(lat, lon, headingDeg - FOV_DEGREES / 2, FOV_RANGE_METERS);
  const right = destinationPoint(
    lat,
    lon,
    headingDeg + FOV_DEGREES / 2,
    FOV_RANGE_METERS
  );
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[[lon, lat], left, right, [lon, lat]]]
    }
  };
}
